/**
 * Billing: the one place money lives.
 *
 * Wallet, Billing and Subscription were three sidebar rows, and a new customer
 * reasonably read that as three different bills. Worse, two of them rendered
 * the same wallet ledger. They are one section now, with a view switch, in the
 * order a person actually asks the questions:
 *
 *   This month  - what do I owe right now
 *   Balance     - what is already paid in
 *   Membership  - what rate am I on, and what would change it
 *
 * The screens themselves are untouched; the duplicate ledger is removed inside
 * BillingScreen, not here.
 *
 * @module web/screens/app/BillingSection
 */
import { useFlatRate } from "../../state/useFlatRate.ts";
import { FlatRatePlan } from "./FlatRatePlan.tsx";
import type { BillingView } from "../../routes.ts";
import { BillingScreen } from "./BillingScreen.tsx";
import { WalletScreen } from "./WalletScreen.tsx";
import { SubscriptionScreen } from "./SubscriptionScreen.tsx";
import { useBrand } from "../../state/BrandContext.tsx";

const VIEWS: { id: BillingView; label: string }[] = [
  { id: "summary", label: "Overview" },
  { id: "wallet", label: "Balance" },
  { id: "membership", label: "Membership" },
];

export function BillingSection({ view, onView }: {
  view: BillingView;
  onView: (v: BillingView) => void;
}) {
  const { brand } = useBrand();
  const { plan, loading, failed, reload } = useFlatRate();
  if (loading) return <p className="t-body-sm" role="status">Loading your billing…</p>;
  if (failed) return <div className="card"><p className="t-body-sm" role="alert">Billing is temporarily unavailable.</p><button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={reload}>Try again</button></div>;
  if (plan) return <FlatRatePlan plan={plan} />;
  return (
    <>
      {brand?.billingBrandId && <p className="t-body-sm" style={{ marginBottom: 16 }}>
        This brand uses the account Owner's card and shared wallet. Its membership plan applies to this brand only.
      </p>}
      <div className="seg-bar fade-in">
        <div className="seg" role="tablist" aria-label="Billing">
          {VIEWS.map(v => (
            <button
              key={v.id}
              role="tab"
              aria-selected={view === v.id}
              className={`seg-btn${view === v.id ? " on" : ""}`}
              onClick={() => onView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === "wallet" ? <WalletScreen />
        : view === "membership" ? <SubscriptionScreen />
        : <BillingScreen />}
    </>
  );
}
